import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { ShippingNotice } from "../components/ShippingNotice";
import { api, money } from "../lib/api";
import { useCart } from "../lib/cart";
import { gaBeginCheckout } from "../lib/analytics";
import { useContent } from "../context/ContentContext";
import { COUNTRIES, regionOfCountry, STATE_REQUIRED } from "../lib/countries";

const emailOk = (e) => /^\S+@\S+\.\S+$/.test(e);

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { p } = useContent();
  const t = p("checkout").labels;
  const hdr = p("checkout").header;
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", postal: "", country: "" });
  const [region, setRegion] = useState("US");
  const [shipping, setShipping] = useState("us_studio");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => { if (items.length === 0) navigate("/cart"); }, [items, navigate]);

  const began = React.useRef(false);
  useEffect(() => {
    if (items.length && !began.current) { began.current = true; gaBeginCheckout(items, subtotal); }
  }, [items, subtotal]);

  const buildOptions = (st, reg) => {
    if (!st) return [];
    if (reg === "US") return [
      { id: "us_studio", label: "U.S. Studio Delivery", price: st.us_studio_price, text: st.us_studio_text, note: st.us_studio_ship_estimate },
      { id: "us_direct", label: "Direct International Delivery", price: st.us_direct_price + (st.us_direct_surcharge || 0), text: st.us_direct_text },
    ];
    if (reg === "EU") return [{ id: "eu", label: "Shipping — Europe", price: st.shipping_eu, text: "Included in the purchase price. Tracked delivery within Europe." }];
    return [{ id: "other", label: "International Shipping", price: st.shipping_other, text: "Included in the purchase price. Tracked international delivery." }];
  };

  // Selecting a country auto-sets the matching destination region.
  const onCountry = (code) => {
    setForm((f) => ({ ...f, country: code }));
    const reg = regionOfCountry(code);
    if (reg) setRegion(reg);
  };

  useEffect(() => {
    const opts = buildOptions(settings, region);
    if (opts.length && !opts.find((o) => o.id === shipping)) setShipping(opts[0].id);
    // eslint-disable-next-line
  }, [region, settings]);

  if (!settings) return <div className="max-w-5xl mx-auto px-5 py-32"><div className="animate-pulse h-96 bg-[#F0EFEA]" /></div>;

  const options = buildOptions(settings, region);
  const shipCost = options.find((o) => o.id === shipping)?.price || 0;
  const total = subtotal + shipCost;
  const shipDisplay = (amt) => (amt && amt > 0 ? money(amt) : t.included_label);

  const countryRegion = form.country ? regionOfCountry(form.country) : null;
  const conflict = !!form.country && countryRegion !== region;
  const stateNeeded = STATE_REQUIRED.has(form.country);
  const errors = {
    name: !form.name.trim(),
    email: !emailOk(form.email),
    line1: !form.line1.trim(),
    city: !form.city.trim(),
    postal: !form.postal.trim(),
    country: !form.country,
    state: stateNeeded && !form.state.trim(),
    method: !options.find((o) => o.id === shipping),
    conflict,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const err = (k) => submitted && errors[k];
  const inputCls = (k) => `bg-transparent py-2 text-sm outline-none border-b ${err(k) ? "border-[#B35D4B]" : "border-[#1C1B1A]"}`;
  const Msg = ({ show, children }) => (show ? <p className="text-xs text-[#B35D4B] mt-1">{children}</p> : null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) {
      toast.error(conflict ? "Please match your destination and country." : "Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const countryName = COUNTRIES.find((c) => c.code === form.country)?.name || form.country;
      const address = [form.line1, form.line2, form.city, form.state, form.postal, countryName].filter(Boolean).join(", ");
      const payload = {
        items: items.map((i) => ({
          kind: i.kind, product_slug: i.product_slug, quantity: i.quantity,
          size_label: i.size_label || null, additional_prints: i.additional_prints || 0,
          instructions: i.instructions || null, upload_id: i.upload_id || null, upload_filename: i.upload_filename || null,
        })),
        shipping_method: shipping, email: form.email, name: form.name, phone: form.phone || null,
        address, address_line1: form.line1, address_line2: form.line2 || null,
        city: form.city, state: form.state || null, postal_code: form.postal,
        country: form.country, country_name: countryName, region,
        origin_url: window.location.origin,
      };
      const r = await api.post("/checkout", payload);
      localStorage.setItem("camino_last_order", JSON.stringify({
        order_id: r.data.order_id,
        value: subtotal,
        shipping: shipCost,
        items: items.map((i) => ({ item_id: i.product_slug || i.kind, item_name: i.title || "Custom Etching", price: Number(i.price) || 0, quantity: i.quantity || 1 })),
      }));
      window.location.href = r.data.checkout_url;
    } catch (err2) {
      toast.error("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const destinations = [{ v: "US", l: t.country_us }, { v: "EU", l: t.country_eu }, { v: "OTHER", l: t.country_other }];

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16" data-testid="checkout-page">
      <Seo title="Checkout — CaminoSapiens" description="Complete your commission." noindex />
      <p className="overline mb-4">{hdr.eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl mb-12">{hdr.heading}</h1>

      <ShippingNotice className="mb-10" />

      <form onSubmit={submit} noValidate className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <p className="overline mb-5">{t.details_label}</p>
            <div className="grid sm:grid-cols-2 gap-x-5 gap-y-6">
              <div>
                <input required placeholder={t.name_placeholder} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" className={`w-full ${inputCls("name")}`} />
                <Msg show={err("name")}>Please enter your full name.</Msg>
              </div>
              <div>
                <input required type="email" placeholder={t.email_placeholder} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" className={`w-full ${inputCls("email")}`} />
                <Msg show={err("email")}>Please enter a valid email.</Msg>
              </div>
              <div className="sm:col-span-2">
                <input placeholder={t.phone_placeholder} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" className={`w-full ${inputCls("phone")}`} />
              </div>
              <div className="sm:col-span-2">
                <input placeholder={t.address1_placeholder} value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} data-testid="checkout-address1" className={`w-full ${inputCls("line1")}`} />
                <Msg show={err("line1")}>Please enter your address.</Msg>
              </div>
              <div className="sm:col-span-2">
                <input placeholder={t.address2_placeholder} value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} data-testid="checkout-address2" className={`w-full ${inputCls("line2")}`} />
              </div>
              <div>
                <input placeholder={t.city_placeholder} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="checkout-city" className={`w-full ${inputCls("city")}`} />
                <Msg show={err("city")}>Please enter your city.</Msg>
              </div>
              <div>
                <input placeholder={t.state_placeholder} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="checkout-state" className={`w-full ${inputCls("state")}`} />
                <Msg show={err("state")}>Please enter your state / province.</Msg>
              </div>
              <div>
                <input placeholder={t.postal_placeholder} value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} data-testid="checkout-postal" className={`w-full ${inputCls("postal")}`} />
                <Msg show={err("postal")}>Please enter your postal / ZIP code.</Msg>
              </div>
              <div>
                <select value={form.country} onChange={(e) => onCountry(e.target.value)} data-testid="checkout-country" className={`w-full cursor-pointer ${inputCls("country")}`}>
                  <option value="">{t.country_placeholder}</option>
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
                <Msg show={err("country")}>Please select your country.</Msg>
              </div>
            </div>
          </div>

          <div>
            <p className="overline mb-5">{t.destination_label}</p>
            <div className="flex flex-wrap gap-3">
              {destinations.map((cc) => (
                <button type="button" key={cc.v} onClick={() => setRegion(cc.v)} data-testid={`country-${cc.v}`}
                  className={`px-5 py-3 text-sm border ${region === cc.v ? "border-[#1C1B1A] bg-[#1C1B1A] text-white" : "border-[#E4E1DB] hover:border-[#1C1B1A]"}`}>{cc.l}</button>
              ))}
            </div>
            {submitted && conflict && (
              <p className="text-xs text-[#B35D4B] mt-3" data-testid="destination-conflict">
                Your selected destination doesn’t match your country ({COUNTRIES.find((c) => c.code === form.country)?.name}). Choose the matching destination or update your country.
              </p>
            )}
          </div>

          <div>
            <p className="overline mb-5">{t.fulfillment_label}</p>
            <div className="space-y-4">
              {options.map((o) => (
                <label key={o.id} data-testid={`shipping-${o.id}`}
                  className={`block border p-5 cursor-pointer transition-colors ${shipping === o.id ? "border-[#1C1B1A] bg-[#F0EFEA]" : "border-[#E4E1DB] hover:border-[#9a9793]"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <input type="radio" name="shipping" checked={shipping === o.id} onChange={() => setShipping(o.id)} className="mt-1.5 accent-[#B35D4B]" />
                      <div>
                        <p className="font-display text-lg">{o.label}</p>
                        <p className="text-sm text-[#4A4947] mt-1 leading-relaxed">{o.text}</p>
                        {o.note && <p className="text-xs text-[#B35D4B] mt-2">{o.note}</p>}
                      </div>
                    </div>
                    <span className="text-sm shrink-0">{shipDisplay(o.price)}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-28 h-fit border border-[#E4E1DB] p-6 bg-[#F0EFEA]">
          <p className="overline mb-4">{t.summary_label}</p>
          {items.map((i) => (
            <div key={i.line_id} className="flex justify-between text-sm text-[#4A4947] py-1">
              <span className="pr-2">{i.title} × {i.quantity}</span><span>{money(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm text-[#4A4947] py-1 border-t border-[#E4E1DB] mt-3 pt-3"><span>{t.shipping_label}</span><span data-testid="checkout-shipping">{shipDisplay(shipCost)}</span></div>
          <p className="text-xs text-[#9a9793] py-1" data-testid="checkout-tax">{t.tax_label}</p>
          <div className="flex justify-between items-baseline mt-4 border-t border-[#1C1B1A] pt-4">
            <span className="font-display text-xl">{t.total_label}</span>
            <span className="font-display text-2xl" data-testid="checkout-total">{money(total)}</span>
          </div>
          <button type="submit" disabled={submitting} data-testid="checkout-pay" className="w-full mt-6 bg-[#1C1B1A] text-[#F9F8F6] py-4 text-sm hover:bg-[#B35D4B] transition-colors disabled:opacity-50">
            {submitting ? t.redirecting_label : t.pay_label}
          </button>
          <p className="text-xs text-[#9a9793] mt-3 text-center">{t.secure_note}</p>
        </div>
      </form>
    </div>
  );
}
